import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const TRACKING_STEPS = [
    { key: 'submitted', label: 'Application Submitted' },
    { key: 'pending', label: 'Under Review' },
    { key: 'completed', label: 'Decision' }
];

const ApplicationStatus = () => {
    const navigate = useNavigate();
    const isReportingManager = localStorage.getItem('isReportingManager') === 'true';
    const isManagingDirector = localStorage.getItem('isManagingDirector') === 'true';
    const showTableView = isReportingManager || isManagingDirector;

    const [resignations, setResignations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalCount: 0,
        limit: 25,
        hasNextPage: false,
        hasPreviousPage: false
    });
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    // const [showDeleteModal, setShowDeleteModal] = useState(false);
    // const [resignationToDelete, setResignationToDelete] = useState(null);
    const searchTimeoutRef = useRef(null);

    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (search === '' || search.trim().length >= 2) {
            searchTimeoutRef.current = setTimeout(() => {
                setDebouncedSearch(search);
            }, 500);
        } else {
            setDebouncedSearch('');
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [search]);

    useEffect(() => {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, [debouncedSearch]);

    useEffect(() => {
        fetchResignations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.currentPage, pagination.limit, debouncedSearch]);

    const fetchResignations = async () => {
        const userId = localStorage.getItem('id');

        if (!userId) {
            toast.error('User ID not found. Please login again.');
            return;
        }

        if (!API_URL) {
            toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                action: 'get-resignations',
                id: userId,
                search: showTableView ? debouncedSearch : '',
                page: showTableView ? pagination.currentPage : 1,
                limit: showTableView ? pagination.limit : 100
            };

            const response = await axios.post(API_URL, payload, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            const result = response.data;

            if (result?.success) {
                setResignations(result.data || []);
                const currentPage = result.page || pagination.currentPage;
                const totalPages = result.totalPages || 1;
                setPagination(prev => ({
                    ...prev,
                    currentPage,
                    totalPages,
                    totalCount: result.total ?? prev.totalCount,
                    hasNextPage: currentPage < totalPages,
                    hasPreviousPage: currentPage > 1
                }));
            } else {
                toast.error(result?.message || 'Failed to fetch resignation applications');
            }
        } catch (error) {
            console.error('Error fetching resignations:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            const day = date.getDate();
            const month = date.toLocaleString('default', { month: 'short' });
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        } catch (error) {
            return dateString;
        }
    };

    const getLatestCommentText = (comments) => {
        if (!Array.isArray(comments) || comments.length === 0) return '-';
        const latest = comments[comments.length - 1];
        return latest?.comment || '-';
    };

    const getStatusClass = (status) => {
        if (!status || status === '-') return 'pending';
        const normalized = String(status).toLowerCase().trim();
        if (normalized.includes('reject')) return 'reject';
        if (normalized.includes('hold')) return 'hold';
        if (normalized.includes('approv')) return 'approved';
        if (normalized.includes('completed') || normalized.includes('complete')) return 'completed';
        if (normalized.includes('pending')) return 'pending';
        return normalized.replace(/\s+/g, '-');
    };

    const getTrackingStepState = (stepKey, status) => {
        const normalized = String(status || '').toLowerCase();

        if (stepKey === 'submitted') {
            return 'completed';
        }

        if (stepKey === 'pending') {
            if (normalized.includes('pending')) return 'active';
            if (normalized.includes('approv') || normalized.includes('reject')) return 'completed';
            return 'upcoming';
        }

        if (stepKey === 'completed') {
            if (normalized.includes('approv') || normalized.includes('reject')) return 'completed';
            return 'upcoming';
        }

        return 'upcoming';
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({ ...prev, currentPage: newPage }));
        }
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value, 10);
        setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
    };

    const handleSearch = (e) => {
        setSearch(e.target.value);
    };

    const getSerialNumber = (index) => {
        return (pagination.currentPage - 1) * pagination.limit + index + 1;
    };

    const isApprovedStatus = (status) => String(status || '').toLowerCase().includes('approv');

    const handleView = (item) => {
        if (!item?.employeeMongoId) {
            toast.error('Unable to open application. Missing employee identifier.');
            return;
        }
        navigate(`/application-status-view/${item.employeeMongoId}`, {
            state: { resignation: item }
        });
    };

    const handleHandover = (item) => {
        if (!item?.employeeMongoId) {
            toast.error('Unable to open assets handover. Missing employee identifier.');
            return;
        }
        navigate('/assets-handover', {
            state: {
                employeeId: item.employeeMongoId,
                employeeName: item.employeeName,
                employeeCode: item.employeeId
            }
        });
    };

    // const handleEdit = (item) => {
    //     if (!item?.id) {
    //         toast.error('Unable to open application. Missing identifier.');
    //         return;
    //     }
    //     navigate('/application-approval', { state: { resignation: item, mode: 'edit' } });
    // };

    // const handleDelete = (item) => {
    //     setResignationToDelete(item);
    //     setShowDeleteModal(true);
    // };

    // const closeDeleteModal = () => {
    //     setShowDeleteModal(false);
    //     setResignationToDelete(null);
    // };

    // const confirmDelete = async () => {
    //     if (!resignationToDelete?.id) return;

    //     const updatedBy = localStorage.getItem('id');
    //     if (!updatedBy) {
    //         toast.error('User ID not found. Please login again.');
    //         closeDeleteModal();
    //         return;
    //     }

    //     if (!API_URL) {
    //         toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
    //         return;
    //     }

    //     setLoading(true);
    //     try {
    //         const payload = {
    //             action: 'delete-resignation',
    //             id: resignationToDelete.id,
    //             updatedBy
    //         };

    //         const response = await axios.post(API_URL, payload, {
    //             headers: { 'Content-Type': 'application/json' }
    //         });

    //         const result = response.data;

    //         if (result?.success) {
    //             toast.success(result?.message || 'Resignation application deleted successfully');
    //             closeDeleteModal();
    //             fetchResignations();
    //         } else {
    //             toast.error(result?.message || 'Failed to delete resignation application');
    //         }
    //     } catch (error) {
    //         console.error('Error deleting resignation:', error);
    //         const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
    //         toast.error(errorMessage);
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const renderPagination = () => {
        if (resignations.length === 0 || pagination.totalPages <= 0) return null;

        return (
            <div className="employee-pagination">
                <div
                    className="pagination-limit-container"
                    style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: 'auto' }}
                >
                    <label htmlFor="resignationPaginationLimit" style={{ margin: 0, fontSize: '14px', color: '#495057' }}>
                        Show:
                    </label>
                    <select
                        id="resignationPaginationLimit"
                        value={pagination.limit}
                        onChange={handleLimitChange}
                        style={{
                            padding: '6px 10px',
                            fontSize: '14px',
                            border: '1px solid #ced4da',
                            borderRadius: '4px',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            minWidth: '70px'
                        }}
                    >
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                        <option value="100">100</option>
                    </select>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(1)}
                        disabled={pagination.currentPage === 1}
                    >
                        «
                    </button>
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                    >
                        ‹
                    </button>
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map((page) => (
                        <button
                            key={page}
                            className={`pagination-btn ${pagination.currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                        >
                            {page}
                        </button>
                    ))}
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        ›
                    </button>
                    <button
                        className="pagination-btn"
                        onClick={() => handlePageChange(pagination.totalPages)}
                        disabled={pagination.currentPage === pagination.totalPages}
                    >
                        »
                    </button>
                </div>
            </div>
        );
    };

    const renderEmployeeTracking = () => (
        <div className="resignation-tracking-list">
            {resignations.length === 0 ? (
                <div className="employee-empty" style={{ padding: '32px', textAlign: 'center' }}>
                    No resignation applications found
                </div>
            ) : (
                resignations.map((item, index) => (
                    <div key={item.id || index} className="card employee-content-card resignation-tracking-card">
                        <div className="card-body">
                            {/* <div className="resignation-tracking-header">
                                <div>
                                    <h4 className="section-title" style={{ marginBottom: '4px' }}>
                                        Application #{getSerialNumber(index)}
                                    </h4>
                                    <p className="section-subtitle" style={{ marginBottom: 0 }}>
                                        Track your resignation request progress
                                    </p>
                                </div>
                                <span className={`employee-status employee-status-${getStatusClass(item.status)}`}>
                                    {item.status || 'Pending'}
                                </span>
                            </div> */}

                            <div className="resignation-tracking-steps">
                                {TRACKING_STEPS.map((step, stepIndex) => {
                                    const state = getTrackingStepState(step.key, item.status);
                                    return (
                                        <div
                                            key={step.key}
                                            className={`resignation-tracking-step resignation-tracking-step-${state}`}
                                        >
                                            <div className="resignation-tracking-step-marker">
                                                {state === 'completed' ? (
                                                    <i className="fas fa-check"></i>
                                                ) : (
                                                    <span>{stepIndex + 1}</span>
                                                )}
                                            </div>
                                            <span className="resignation-tracking-step-label">{step.label}</span>
                                            {stepIndex < TRACKING_STEPS.length - 1 && (
                                                <div className="resignation-tracking-step-line" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="employee-detail-grid two-column">
                                <div className="employee-detail-item">
                                    <span className="employee-detail-label">Resignation Date</span>
                                    <span className="employee-detail-value">{formatDate(item.resignationDate)}</span>
                                </div>
                                <div className="employee-detail-item">
                                    <span className="employee-detail-label">Last Working Date</span>
                                    <span className="employee-detail-value">{formatDate(item.lastWorkingDate)}</span>
                                </div>
                                <div className="employee-detail-item">
                                    <span className="employee-detail-label">Notice Period</span>
                                    <span className="employee-detail-value">{item.noticePeriod ?? '-'}</span>
                                </div>
                                <div className="employee-detail-item">
                                    <span className="employee-detail-label">Status</span>
                                    <span className="employee-detail-value">{item.status || '-'}</span>
                                </div>
                                <div className="employee-detail-item" style={{ gridColumn: '1 / -1' }}>
                                    <span className="employee-detail-label">Reason</span>
                                    <span className="employee-detail-value">{item.reason || '-'}</span>
                                </div>

                                <div className="employee-detail-item" style={{ gridColumn: '1 / -1' }}>
                                    <span className="employee-detail-label">Comments</span>
                                    <span className="employee-detail-value">{getLatestCommentText(item.comments)}</span>
                                </div>
                                
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );

    const renderManagerTable = () => (
        <>
            <div className="employee-table-wrapper">
                <table className="employee-table">
                    <thead>
                        <tr>
                            <th>SI No.</th>
                            <th>Employee Name</th>
                            <th>Employee ID</th>
                            <th>Department</th>
                            <th>Resignation Date</th>
                            <th>Reason</th>
                            <th>Status</th>
                            <th>Last Working Date</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {resignations.length === 0 ? (
                            <tr>
                                <td colSpan="9" className="employee-empty">
                                    No resignation applications found
                                </td>
                            </tr>
                        ) : (
                            resignations.map((item, index) => (
                                <tr key={item.id || index}>
                                    <td>{getSerialNumber(index)}</td>
                                    <td>{item.employeeName || '-'}</td>
                                    <td>{item.employeeId || '-'}</td>
                                    <td>{item.department || '-'}</td>
                                    <td>{formatDate(item.resignationDate)}</td>
                                    <td>{item.reason || '-'}</td>
                                    <td>
                                        <span className={`employee-status employee-status-${getStatusClass(item.status)}`}>
                                            {item.status || '-'}
                                        </span>
                                    </td>
                                    <td>{formatDate(item.lastWorkingDate)}</td>
                                    <td>
                                        <div className="employee-actions">
                                            <button
                                                className="employee-action-btn employee-action-view"
                                                onClick={() => handleView(item)}
                                                title="View"
                                            >
                                                <i className="fa-solid fa-eye"></i>
                                            </button>
                                            {isApprovedStatus(item.status) && (
                                                <button
                                                    className="employee-action-btn employee-action-handover"
                                                    onClick={() => handleHandover(item)}
                                                    title="Assets Handover"
                                                >
                                                    <i className="fa-solid fa-hand-holding"></i>
                                                </button>
                                            )}
                                            {/* <button
                                                className="employee-action-btn employee-action-edit"
                                                onClick={() => handleEdit(item)}
                                                title="Edit"
                                            >
                                                <i className="fa-solid fa-pencil"></i>
                                            </button> */}
                                            
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            {renderPagination()}
        </>
    );

    return (
        <div className="employee-form">
            <section className="content">
                <div className="container-fluid">
                    <div className="row mb-3 employee-header-row">
                        <div className={showTableView ? 'col-md-12' : 'col-md-8'}>
                            <h1 className="employee-title">
                                Application Status
                                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
                            </h1>
                            <p className="employee-description">
                                {showTableView
                                    ? 'Review resignation applications submitted by employees in your organization.'
                                    : 'Track your resignation application progress and approval status.'}
                            </p>
                        </div>
                       
                    </div>

                    <div className="card employee-content-card">
                        <div className="card-body">
                            {showTableView && (
                                <div className="employee-search-container" style={{ marginBottom: '16px' }}>
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            className="form-input employee-search-input"
                                            placeholder="Search applications..."
                                            value={search}
                                            onChange={handleSearch}
                                        />
                                    </div>
                                </div>
                            )}

                            {loading ? (
                                <Loading size={120} />
                            ) : showTableView ? (
                                renderManagerTable()
                            ) : (
                                renderEmployeeTracking()
                            )}
                        </div>
                    </div>
                </div>
            </section>


            {/* {showDeleteModal && (
                <div className="modal-overlay" onClick={closeDeleteModal}>
                    <div className="modal-content1" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                            <button className="modal-close" onClick={closeDeleteModal}>
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>
                                Are you sure you want to delete the resignation application for{' '}
                                <strong>{resignationToDelete?.employeeName || 'this employee'}</strong>?
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-primary" onClick={confirmDelete} disabled={loading}>
                                Yes
                            </button>
                        </div>
                    </div>
                </div>
            )} */}

            <style>{`
                .resignation-tracking-list {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                .resignation-tracking-card {
                    margin-bottom: 0;
                }
                .resignation-tracking-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    gap: 16px;
                    margin-bottom: 24px;
                }
                .resignation-tracking-steps {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 28px;
                    padding: 0 8px;
                }
                .resignation-tracking-step {
                    position: relative;
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                    min-width: 0;
                }
                .resignation-tracking-step-marker {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 13px;
                    font-weight: 600;
                    background: #e5e7eb;
                    color: #6b7280;
                    z-index: 1;
                }
                .resignation-tracking-step-completed .resignation-tracking-step-marker {
                    background: #0094a9;
                    color: #fff;
                }
                .resignation-tracking-step-active .resignation-tracking-step-marker {
                    background: #fff;
                    border: 2px solid #0094a9;
                    color: #0094a9;
                }
                .resignation-tracking-step-label {
                    margin-top: 10px;
                    font-size: 12px;
                    color: #6b7280;
                    font-weight: 500;
                    padding: 0 4px;
                }
                .resignation-tracking-step-completed .resignation-tracking-step-label,
                .resignation-tracking-step-active .resignation-tracking-step-label {
                    color: #111827;
                }
                .resignation-tracking-step-line {
                    position: absolute;
                    top: 18px;
                    left: calc(50% + 18px);
                    width: calc(100% - 36px);
                    height: 2px;
                    background: #e5e7eb;
                }
                .resignation-tracking-step-completed .resignation-tracking-step-line {
                    background: #0094a9;
                }
                .employee-status-pending {
                    background-color: #fef3c7;
                    color: #92400e;
                }
                .employee-status-hold {
                    background-color: #e0e7ff;
                    color: #3730a3;
                }
                .employee-status-approved {
                    background-color: #d1fae5;
                    color: #065f46;
                }
                .employee-status-reject {
                    background-color: #fee2e2;
                    color: #991b1b;
                }
                .employee-status-completed {
                    background-color: #dbeafe;
                    color: #1e40af;
                }
                .employee-action-handover {
                    background-color: #6366f1;
                    color: #ffffff;
                }
                .employee-action-handover:hover {
                    background-color: #4f46e5;
                }
                @media (max-width: 768px) {
                    .resignation-tracking-steps {
                        flex-direction: column;
                        gap: 16px;
                    }
                    .resignation-tracking-step-line {
                        display: none;
                    }
                    .resignation-tracking-header {
                        flex-direction: column;
                    }
                }
            `}</style>
        </div>
    );
};

export default ApplicationStatus;
